import { supabase } from '../lib/supabase'

export interface EmployeeRow {
  id: string
  user_profile_id?: string | null
  employee_number?: string | null
  first_name: string
  last_name: string
  email?: string | null
  phone?: string | null
  employment_type?: string | null
  is_available_for_scheduling?: boolean | null
  is_active?: boolean | null
  created_at?: string
  updated_at?: string
  linked_user?: {
    id: string
    email: string
    first_name?: string | null
    last_name?: string | null
  } | null
}

export interface CrewRow {
  id: string
  name: string
  description?: string | null
  lead_employee_id?: string | null
  is_active?: boolean | null
  is_subcontractor?: boolean | null
  created_at?: string
  updated_at?: string
  member_ids?: string[]
  lead?: { id: string; first_name: string; last_name: string } | null
}

export type EmployeeInput = {
  first_name: string
  last_name: string
  email?: string | null
  phone?: string | null
  employee_number?: string | null
  employment_type?: string | null
  is_available_for_scheduling?: boolean
  is_active?: boolean
  user_profile_id?: string | null
}

export type CrewInput = {
  name: string
  description?: string | null
  lead_employee_id?: string | null
  is_active?: boolean
  is_subcontractor?: boolean
  member_ids?: string[]
}

export async function listEmployees(): Promise<EmployeeRow[]> {
  const { data, error } = await supabase
    .from('employees')
    .select(
      'id, user_profile_id, employee_number, first_name, last_name, email, phone, employment_type, is_available_for_scheduling, is_active, created_at, updated_at'
    )
    .order('first_name')
  if (error) throw error

  const rows = (data || []) as EmployeeRow[]
  const profileIds = rows.map((r) => r.user_profile_id).filter(Boolean) as string[]
  if (profileIds.length === 0) return rows

  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('id, email, first_name, last_name')
    .in('id', profileIds)

  const byId = new Map((profiles || []).map((p: any) => [p.id, p]))
  return rows.map((r) => ({
    ...r,
    linked_user: r.user_profile_id ? byId.get(r.user_profile_id) || null : null,
  }))
}

export async function createEmployee(input: EmployeeInput): Promise<EmployeeRow> {
  const { data, error } = await supabase
    .from('employees')
    .insert({
      first_name: input.first_name.trim(),
      last_name: input.last_name.trim(),
      email: input.email?.trim() || null,
      phone: input.phone?.trim() || null,
      employee_number: input.employee_number?.trim() || null,
      employment_type: input.employment_type || 'full_time',
      is_available_for_scheduling: input.is_available_for_scheduling !== false,
      is_active: input.is_active !== false,
      user_profile_id: input.user_profile_id || null,
    })
    .select()
    .single()
  if (error) throw error
  return data as EmployeeRow
}

export async function updateEmployee(id: string, input: Partial<EmployeeInput>): Promise<EmployeeRow> {
  const patch: Record<string, unknown> = { updated_at: new Date().toISOString() }
  if (input.first_name !== undefined) patch.first_name = input.first_name.trim()
  if (input.last_name !== undefined) patch.last_name = input.last_name.trim()
  if (input.email !== undefined) patch.email = input.email?.trim() || null
  if (input.phone !== undefined) patch.phone = input.phone?.trim() || null
  if (input.employee_number !== undefined) patch.employee_number = input.employee_number?.trim() || null
  if (input.employment_type !== undefined) patch.employment_type = input.employment_type || 'full_time'
  if (input.is_available_for_scheduling !== undefined) {
    patch.is_available_for_scheduling = input.is_available_for_scheduling
  }
  if (input.is_active !== undefined) patch.is_active = input.is_active
  if (input.user_profile_id !== undefined) patch.user_profile_id = input.user_profile_id || null

  const { data, error } = await supabase.from('employees').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data as EmployeeRow
}

export async function listCrews(): Promise<CrewRow[]> {
  const { data: crews, error } = await supabase
    .from('crews')
    .select(
      'id, name, description, lead_employee_id, is_active, is_subcontractor, created_at, updated_at'
    )
    .order('name')
  if (error) throw error

  const list = (crews || []) as CrewRow[]
  if (list.length === 0) return []

  const crewIds = list.map((c) => c.id)
  const { data: members } = await supabase
    .from('crew_members')
    .select('crew_id, employee_id')
    .in('crew_id', crewIds)

  const membersByCrew = new Map<string, string[]>()
  for (const m of members || []) {
    const arr = membersByCrew.get(m.crew_id) || []
    arr.push(m.employee_id)
    membersByCrew.set(m.crew_id, arr)
  }

  const leadIds = list.map((c) => c.lead_employee_id).filter(Boolean) as string[]
  let leads = new Map<string, { id: string; first_name: string; last_name: string }>()
  if (leadIds.length > 0) {
    const { data: emp } = await supabase
      .from('employees')
      .select('id, first_name, last_name')
      .in('id', leadIds)
    leads = new Map((emp || []).map((e: any) => [e.id, e]))
  }

  return list.map((c) => ({
    ...c,
    member_ids: membersByCrew.get(c.id) || [],
    lead: c.lead_employee_id ? leads.get(c.lead_employee_id) || null : null,
  }))
}

export async function createCrew(input: CrewInput): Promise<CrewRow> {
  const { data, error } = await supabase
    .from('crews')
    .insert({
      name: input.name.trim(),
      description: input.description?.trim() || null,
      lead_employee_id: input.lead_employee_id || null,
      is_active: input.is_active !== false,
      is_subcontractor: input.is_subcontractor === true,
    })
    .select()
    .single()
  if (error) throw error

  const crew = data as CrewRow
  if (input.member_ids && input.member_ids.length > 0) {
    await replaceCrewMembers(crew.id, input.member_ids)
  }
  return { ...crew, member_ids: input.member_ids || [] }
}

export async function updateCrew(id: string, input: CrewInput): Promise<CrewRow> {
  const { data, error } = await supabase
    .from('crews')
    .update({
      name: input.name.trim(),
      description: input.description?.trim() || null,
      lead_employee_id: input.lead_employee_id || null,
      is_active: input.is_active !== false,
      is_subcontractor: input.is_subcontractor === true,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error

  if (input.member_ids !== undefined) {
    await replaceCrewMembers(id, input.member_ids)
  }
  return { ...(data as CrewRow), member_ids: input.member_ids || [] }
}

export async function replaceCrewMembers(crewId: string, employeeIds: string[]): Promise<void> {
  const unique = Array.from(new Set(employeeIds.filter(Boolean)))
  const { error: delErr } = await supabase.from('crew_members').delete().eq('crew_id', crewId)
  if (delErr) throw delErr
  if (unique.length === 0) return
  const { error: insErr } = await supabase.from('crew_members').insert(
    unique.map((employee_id) => ({ crew_id: crewId, employee_id }))
  )
  if (insErr) throw insErr
}

/** Find employee linked to a user profile (Team login badge). */
export async function findEmployeeByUserProfileId(
  userProfileId: string
): Promise<Pick<EmployeeRow, 'id' | 'first_name' | 'last_name'> | null> {
  const { data, error } = await supabase
    .from('employees')
    .select('id, first_name, last_name')
    .eq('user_profile_id', userProfileId)
    .maybeSingle()
  if (error) throw error
  return data
}
